import React, { createContext, useState, useEffect, useMemo } from 'react';
import { testMasterService } from '../services/testMasterService';
import { toast } from 'react-toastify';

export const TestMasterContext = createContext();

export const TestMasterProvider = ({ children }) => {
  const [allTestMasters, setAllTestMasters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTestMaster, setSelectedTestMaster] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('edit'); // 'edit' or 'create'
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    gender: '',
    clinicalDataCategory: '',
    healthScoreCategory: '',
    nonBiomarker: false
  });
  
  // Load all test masters once
  const loadTestMasters = async () => {
    setLoading(true);
    try {
      const response = await testMasterService.getTestMasters();
      if (response.data && response.data.success && response.data.data) {
        setAllTestMasters(response.data.data);
        return response.data.data;
      }
      return [];
    } catch (error) {
      console.error('Error loading test masters:', error);
      toast.error('Failed to load test masters');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Load test masters on component mount (only if user is authenticated)
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      loadTestMasters();
    }
  }, []);

  // Update filters without making API calls
  const updateFilters = (newFilters) => {
    setFilters(prevFilters => ({ ...prevFilters, ...newFilters }));
  };

  // Create a new test master
  const createTestMaster = async (testMasterData) => {
    setLoading(true);
    try {
      const response = await testMasterService.createTestMaster(testMasterData);
      if (response.data && response.data.success) {
        toast.success('Test master created successfully');
        await loadTestMasters(); // Reload the list
        return response.data;
      }
      return null;
    } catch (error) {
      console.error('Error creating test master:', error);
      toast.error('Failed to create test master');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Update an existing test master
  const updateTestMaster = async (testId, testMasterData) => {
    setLoading(true);
    try {
      const response = await testMasterService.updateTestMaster(testId, testMasterData);
      if (response.data && response.data.success) {
        toast.success('Test master updated successfully');
        await loadTestMasters(); // Reload the list
        return response.data;
      }
      return null;
    } catch (error) {
      console.error('Error updating test master:', error);
      toast.error('Failed to update test master');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Delete a test master
  const deleteTestMaster = async (testId) => {
    setLoading(true);
    try {
      const response = await testMasterService.deleteTestMaster(testId);
      if (response.data && response.data.success) {
        toast.success('Test master deleted successfully');
        await loadTestMasters(); // Reload the list
        return response.data;
      }
      return null;
    } catch (error) {
      console.error('Error deleting test master:', error);
      toast.error('Failed to delete test master');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Open modal for editing
  const openEditModal = (testMaster) => {
    setSelectedTestMaster(testMaster);
    setModalMode('edit');
    setIsModalOpen(true);
  };

  // Open modal for creating
  const openCreateModal = () => {
    setSelectedTestMaster(null);
    setModalMode('create');
    setIsModalOpen(true);
  };

  // Close modal
  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedTestMaster(null);
  };

  // Filter and sort test masters locally based on current filters
  const filteredTestMasters = useMemo(() => {
    console.log('Filtering with nonBiomarker:', filters.nonBiomarker);
    // First filter the test masters
    const filtered = allTestMasters.filter(test => {
      // Search filter
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase();
        const matchesSearch = 
          (test.biomarker_name && test.biomarker_name.toLowerCase().includes(searchTerm)) || 
          (test.test_code && test.test_code.toLowerCase().includes(searchTerm)) ||
          (test.description && test.description.toLowerCase().includes(searchTerm));
        
        if (!matchesSearch) return false;
      }
      
      // Category filter
      if (filters.category && filters.category !== '') {
        // Handle null category or category mismatch
        if (!test.category || test.category !== filters.category) return false;
      }
      
      // Gender filter
      if (filters.gender && filters.gender !== '') {
        if (!test.gender || test.gender !== filters.gender) return false;
      }
      
      // Clinical Data Category filter
      if (filters.clinicalDataCategory && filters.clinicalDataCategory !== '') {
        // Check if test has clinical_data_category and if profile_name matches
        if (!test.clinical_data_category || 
            !test.clinical_data_category.profile_name || 
            test.clinical_data_category.profile_name !== filters.clinicalDataCategory) {
          return false;
        }
      }
      
      // Health Score Category filter
      if (filters.healthScoreCategory && filters.healthScoreCategory !== '') {
        // Check if test has health_score_weightage and if any item has matching name
        if (!test.health_score_weightage || 
            !Array.isArray(test.health_score_weightage) || 
            !test.health_score_weightage.some(item => item.name === filters.healthScoreCategory)) {
          return false;
        }
      }
      
      // Non-Biomarker filter
      // Default: show only non-biomarker: false (hide non-biomarkers)
      // When checked: show only non-biomarker: true (show non-biomarkers)
      const isNonBiomarker = test.non_biomarker === true || test.non_biomarker === 1 || test.non_biomarker === '1';
      
      if (filters.nonBiomarker) {
        // Show only non-biomarkers
        if (!isNonBiomarker) {
          return false;
        }
      } else {
        // Show only regular biomarkers
        if (isNonBiomarker) {
          return false;
        }
      }
      
      return true;
    });
    
    // Sort by biomarker_name, gender, and age (agerange_min)
    return filtered.sort((a, b) => {
      // First sort by biomarker_name
      // Handle null/undefined values and convert to lowercase for case-insensitive comparison
      const biomarkerNameA = (a.biomarker_name || '').toLowerCase();
      const biomarkerNameB = (b.biomarker_name || '').toLowerCase();
      
      if (biomarkerNameA < biomarkerNameB) return -1;
      if (biomarkerNameA > biomarkerNameB) return 1;
      
      // If biomarker_name is the same, sort by gender
      // Convert both to lowercase for case-insensitive comparison
      const genderA = (a.gender || '').toLowerCase();
      const genderB = (b.gender || '').toLowerCase();
      
      if (genderA < genderB) return -1;
      if (genderA > genderB) return 1;
      
      // If gender is also the same, sort by agerange_min
      // Convert to numbers and handle null/undefined values
      const ageA = a.agerange_min !== null && a.agerange_min !== undefined ? Number(a.agerange_min) : 0;
      const ageB = b.agerange_min !== null && b.agerange_min !== undefined ? Number(b.agerange_min) : 0;
      
      return ageA - ageB; // Ascending order by age
    });
  }, [allTestMasters, filters]);

  return (
    <TestMasterContext.Provider value={{ 
      testMasters: filteredTestMasters,
      filteredTestMasters, // Explicitly expose this for pagination
      allTestMasters,
      selectedTestMaster,
      isModalOpen,
      modalMode,
      loading,
      filters,
      loadTestMasters,
      updateFilters,
      createTestMaster,
      updateTestMaster,
      deleteTestMaster,
      openEditModal,
      openCreateModal,
      closeModal
    }}>
      {children}
    </TestMasterContext.Provider>
  );
};
